import Link from 'next/link';
import { NextSeo } from 'next-seo';

import {
  DocsArticleLayout,
  DocsCallout,
  DocsCodeBlock,
  DocsStep,
  DocsSteps,
} from '@/components/modules/Docs';
import {
  AgentProtocolReference,
  CHATCO_MCP_URL,
  ChatcoDisconnect,
  CONNECTOR_STATUS_CHECKED,
  ConnectorCreditsTable,
  ConnectorErrors,
  ConnectorPrerequisites,
  ConnectorReferences,
  ConnectorStatusPanel,
  ConsentExplainer,
  FirstRunWalkthrough,
  getConnectorHost,
  getConnectorNeighbours,
} from '@/components/modules/Docs/connectors';
import PageLink from '@/utils/PageLink';

const host = getConnectorHost('grok-build');

const tableOfContents = [
  { id: 'availability', title: '1. Availability' },
  { id: 'before-you-start', title: '2. Before you start' },
  { id: 'install-plugin', title: '3. Install the plugin' },
  { id: 'mcp-only', title: '4. Alternative: server only' },
  { id: 'approve', title: '5. Approve in Chat.co' },
  { id: 'first-answer', title: '6. Your first cited answer' },
  { id: 'credits', title: '7. Message credits' },
  { id: 'troubleshooting', title: '8. Common errors' },
  { id: 'disconnect', title: '9. Disconnect' },
  { id: 'developer-reference', title: '10. For developers & agents' },
  { id: 'references', title: '11. References' },
];

const PACKAGE_REPO = 'https://github.com/01-Atlas/chatco-experts';
const CATALOG_PR = 'https://github.com/xai-org/plugin-marketplace/pull/836';
/** Commit pinned in the xAI catalogue submission. */
const CATALOG_PIN = '46413e8c1ca3c971dda705c199d7567fe3c434e5';

const CLONE = `git clone ${PACKAGE_REPO}.git ~/.grok/plugins/chatco-experts`;
const PIN = `git -C ~/.grok/plugins/chatco-experts checkout ${CATALOG_PIN}`;
const MCP_ADD = `grok mcp add --transport http chatco ${CHATCO_MCP_URL}`;

const stepLink = 'font-medium text-primary-700 underline';

const GrokBuildConnectorGuide = () => (
  <>
    <NextSeo
      title="Use Chat.co experts in Grok Build"
      description="Install the public Chat.co plugin in Grok Build, sign in with OAuth, and get cited answers from your own Chat.co experts in the terminal."
      canonical={`https://chat.co${host.href}`}
      openGraph={{
        url: `https://chat.co${host.href}`,
        title: 'Use Chat.co experts in Grok Build | Chat.co Docs',
        description: 'Install the Chat.co plugin in Grok Build and get cited answers.',
        type: 'article',
        article: {
          publishedTime: '2026-09-30T00:00:00Z',
          modifiedTime: '2026-09-30T00:00:00Z',
          section: 'Integrations',
          tags: ['grok build', 'xai', 'plugin', 'mcp', 'experts', 'citations'],
        },
      }}
    />
    <DocsArticleLayout
      category="Experts in AI apps"
      categoryColor="text-orange-700"
      title="Use your Chat.co experts in Grok Build"
      description="Install the public Chat.co plugin in xAI’s terminal agent, or add the server on its own, and ask your experts for cited answers."
      readingTime="8 min read"
      lastUpdated="September 2026"
      tableOfContents={tableOfContents}
      {...getConnectorNeighbours('grok-build')}
      keyTakeaways={[
        'The Chat.co plugin is public on GitHub; you install it manually for now.',
        'Its entry in xAI’s plugin marketplace is an open pull request, not a published listing.',
        'We tested the plugin in Grok Build: sign-in plus five functional cases, including sources.',
        'Sign-in is OAuth through /mcps. The plugin contains no credentials.',
      ]}>
      <section id="availability" className="scroll-mt-28">
        <h2>1. Availability</h2>
        <ConnectorStatusPanel host={host} />
        <p>
          The plugin lives in the public{' '}
          <a href={PACKAGE_REPO} target="_blank" rel="noopener noreferrer">
            01-Atlas/chatco-experts
          </a>{' '}
          repository. It bundles the Chat.co server entry and a skill that tells Grok how to use the
          tools. It holds no backend code and no credentials.
        </p>
        <p>
          Our submission to xAI&apos;s plugin marketplace is{' '}
          <a href={CATALOG_PR} target="_blank" rel="noopener noreferrer">
            pull request #836
          </a>
          . As of {CONNECTOR_STATUS_CHECKED} it is open and not merged. Until xAI merges it, Chat.co
          does not appear in Grok Build&apos;s <code>/marketplace</code>. Merging is xAI&apos;s
          decision.
        </p>
        <p>
          We installed the pinned plugin in Grok Build and passed sign-in plus five functional
          cases: listing experts, a cited answer, a follow-up, a direct search and opening a source.
          For grok.com in the browser, see <Link href={PageLink.docsExternalExpertsGrok}>Grok</Link>
          .
        </p>
      </section>

      <section id="before-you-start" className="scroll-mt-28">
        <h2>2. Before you start</h2>
        <ConnectorPrerequisites>
          <li>
            <strong>Grok Build installed and signed in</strong>, as described in xAI&apos;s Grok
            Build documentation.
          </li>
          <li>
            <strong>Git</strong>, to download the plugin.
          </li>
        </ConnectorPrerequisites>
      </section>

      <section id="install-plugin" className="scroll-mt-28">
        <h2>3. Install the plugin</h2>
        <p>
          Grok Build loads plugins from <code>~/.grok/plugins/</code>. On Windows, <code>~</code> is
          your user profile folder.
        </p>
        <DocsSteps>
          <DocsStep title="Download the plugin">
            <DocsCodeBlock code={CLONE} language="bash" />
            <p>
              Optional: to run exactly the version submitted to xAI&apos;s catalogue, check out its
              pinned commit:
            </p>
            <DocsCodeBlock code={PIN} language="bash" />
          </DocsStep>
          <DocsStep title="Start Grok Build and check the plugin">
            <p>
              Start <code>grok</code> and open <code>/plugins</code>.{' '}
              <strong>chatco-experts</strong> should be listed. To try it for one session without
              installing, start Grok Build with <code>--plugin-dir</code> pointing at the downloaded
              folder instead.
            </p>
          </DocsStep>
          <DocsStep title="Sign in to Chat.co">
            <p>
              Open <code>/mcps</code>, select <strong>chatco</strong> and press <kbd>i</kbd> to
              authenticate. Your browser opens the Chat.co approval page; approve it as described in{' '}
              <a href="#approve" className={stepLink}>
                Approve in Chat.co
              </a>
              . Grok stores the resulting sign-in under <code>~/.grok/</code>.
            </p>
          </DocsStep>
        </DocsSteps>
        <DocsCallout variant="note" title="Read before you install">
          Plugins can include skills, hooks and servers that run on your machine. This one contains
          a server entry pointing at <code>{CHATCO_MCP_URL}</code> and a skill. Its source is short;
          review it on GitHub before installing, as you would any plugin.
        </DocsCallout>
      </section>

      <section id="mcp-only" className="scroll-mt-28">
        <h2>4. Alternative: add the server only</h2>
        <p>
          If you prefer not to install a plugin, Grok Build&apos;s documented{' '}
          <code>grok mcp add</code> command adds the server on its own, without the skill:
        </p>
        <DocsCodeBlock code={MCP_ADD} language="bash" />
        <p>
          Sign in the same way: <code>/mcps</code>, select chatco, press <kbd>i</kbd>. We tested the
          plugin install, not this route; it uses the same server and sign-in.
        </p>
      </section>

      <section id="approve" className="scroll-mt-28">
        <h2>5. Approve in Chat.co</h2>
        <ConsentExplainer />
      </section>

      <section id="first-answer" className="scroll-mt-28">
        <h2>6. Your first cited answer</h2>
        <FirstRunWalkthrough where="a Grok Build session" />
      </section>

      <section id="credits" className="scroll-mt-28">
        <h2>7. Message credits</h2>
        <ConnectorCreditsTable />
      </section>

      <section id="troubleshooting" className="scroll-mt-28">
        <h2>8. Common errors</h2>
        <ConnectorErrors
          hostRows={[
            [
              'chatco-experts is not in /plugins',
              'Check the folder is ~/.grok/plugins/chatco-experts and contains .grok-plugin/plugin.json, then restart grok.',
            ],
            [
              'Chat.co is not in /marketplace',
              'Expected: the catalogue entry has not been merged. Install manually as above.',
            ],
            [
              'chatco is listed in /mcps but its tools fail',
              'Select it in /mcps and press i to sign in again. Press r to refresh after editing config.',
            ],
          ]}
        />
      </section>

      <section id="disconnect" className="scroll-mt-28">
        <h2>9. Disconnect</h2>
        <ChatcoDisconnect>
          <p>
            In Grok Build, turn the server off in <code>/mcps</code> (select it and press{' '}
            <kbd>Space</kbd>), then delete the <code>chatco-experts</code> folder from{' '}
            <code>~/.grok/plugins/</code>. If you added the server on its own, run:
          </p>
          <DocsCodeBlock code="grok mcp remove chatco" language="bash" />
        </ChatcoDisconnect>
      </section>

      <section id="developer-reference" className="scroll-mt-28">
        <h2>10. For developers &amp; agents</h2>
        <AgentProtocolReference />
      </section>

      <section id="references" className="scroll-mt-28">
        <h2>11. References</h2>
        <ConnectorReferences
          vendorLinks={[
            { label: 'Chat.co experts plugin on GitHub', href: PACKAGE_REPO },
            {
              label: 'xAI plugin marketplace submission (pull request #836)',
              href: CATALOG_PR,
            },
            {
              label: 'xAI: Grok Build MCP servers',
              href: 'https://docs.x.ai/build/features/mcp-servers',
            },
            {
              label: 'xAI: Skills, plugins & marketplaces',
              href: 'https://docs.x.ai/build/features/skills-plugins-marketplaces',
            },
          ]}
        />
      </section>
    </DocsArticleLayout>
  </>
);

export default GrokBuildConnectorGuide;
